<template>
  <sidebar-row v-bind="$attrs">
    <v-row no-gutters class="pb-4">
      <v-col v-if="isFetching">
        <main-loader />
      </v-col>
      <v-col v-else>
        <div class="text-left text-body-2 font-weight-medium font--text pb-4">{{ $i18n.t('sidebar.legend') }}</div>
        <v-list class="py-0">
          <v-list-item
            class="pa-0 pb-1"
            v-for="(layer, index) in computedLegendItems"
            :key="index"
            style="min-height: 30px"
          >
            <template>
              <v-list-item-action style="margin: 0 5px 0 0; align-self: start" v-if="!layer.uniques && !layer.ranges">
                <v-row no-gutters class="align-center">
                  <v-col cols="12" style="padding-bottom: 2px">
                    <template>
                      <layer-style-preview-icon is-icon :layer-type="layer.layerType" :layer-style="layer.style" />
                    </template>
                  </v-col>
                </v-row>
              </v-list-item-action>
              <v-list-item-content class="py-0">
                <v-col class="pa-0">
                  <v-row v-if="layer.uniques" no-gutters>
                    <v-col class="pa-0">
                      <v-list>
                        <v-list-item
                          class="px-0 pb-1"
                          v-for="unique of layer.uniques"
                          :key="unique.value"
                          style="max-height: 30px; min-height: 0"
                        >
                          <v-list-item-action class="ma-0 pr-2">
                            <layer-style-preview-icon is-icon :layer-type="layer.layerType" :layer-style="unique" />
                          </v-list-item-action>
                          <v-list-item-title style="text-align: left" class="text-body-2">
                            {{
                              unique.label || unique.label === ''
                                ? unique.label
                                : unique.value || $i18n.t('default.others')
                            }}
                          </v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-col>
                  </v-row>
                  <v-row v-else-if="layer.ranges" no-gutters>
                    <v-col class="pa-0">
                      <v-list>
                        <v-list-item
                          class="px-0 pb-1"
                          v-for="range of layer.ranges"
                          :key="range.value"
                          style="max-height: 30px; min-height: 0"
                        >
                          <v-list-item-action class="ma-0 pr-2">
                            <layer-style-preview-icon is-icon :layer-type="layer.layerType" :layer-style="range" />
                          </v-list-item-action>
                          <v-list-item-title style="text-align: left" class="text-body-2">
                            {{ range.label || $i18n.t('default.others') }}
                          </v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-col>
                  </v-row>
                  <v-row style="min-height: 27px" v-else no-gutters>
                    <v-list-item-title class="text-body-2" style="text-align: left">
                      {{ layer.name }}
                    </v-list-item-title>
                  </v-row>
                </v-col>
              </v-list-item-content>
            </template>
          </v-list-item>
        </v-list>
      </v-col>
    </v-row>
  </sidebar-row>
</template>

<script>
import SidebarRow from '@/components/SidebarRow';
import { call, get } from 'vuex-pathify';
import LayerStylePreviewIcon from '@/components/LayerStylePreviewIcon';
import MainLoader from '@/components/MainLoader';

import stylePreviewUtilities from '@/mixins/stylePreviewUtilities';

export default {
  name: 'ModuleLegend',
  mixins: [stylePreviewUtilities],
  components: { MainLoader, LayerStylePreviewIcon, SidebarRow },
  props: {
    moduleName: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    isFetching: true,
    currentStyle: undefined,
    legendItems: [],
  }),
  computed: {
    currentModuleStyle: get('styles/currentStyle@:dataSource'),
    layerId: get('admin/modulesMapping@:moduleName.layer_id'),
    dataSource: get('admin/modulesMapping@:moduleName.datasource_name'),
    moduleLayer: get('layers/featuresLayers@:layerId'),
    computedLegendItems() {
      return this.legendItems.length === 0
        ? []
        : this.legendItems.map(item => {
            return {
              ...item,
              name: item?.isDefault ? `${this.$i18n.t(`sidebar.${this.moduleName}Module.defaultStyle`)}` : item.name,
              iconSrc: this.getStyleExternalGraphicIcon(item.style ? item.style.externalGraphic : null),
              color: this.getStyleIconColor(item.style),
              layerType: this.getModuleLayerType(item),
              uniques: item.style && item.style.uniques ? this.getUniques(item.style) : null,
              ranges: item.style && item.style.ranges ? this.getRanges(item.style) : null,
            };
          });
    },
  },
  methods: {
    getFeaturesLayer: call('layers/getFeaturesLayer'),

    async initLegendItems() {
      const { style_web, name, geometry_type } = this.moduleLayer;
      this.currentStyle = {
        name,
        geometry_type,
        style: style_web,
      };
    },
    getUniques(style) {
      const uniques = style.uniques;
      const defaultStyle = { ...style };
      defaultStyle.color = this.getStyleIconColor(defaultStyle);
      defaultStyle.icon =
        defaultStyle.externalGraphic && this.getStyleExternalGraphicIcon(defaultStyle.externalGraphic);
      if (!uniques.values) {
        return;
      }
      return Object.keys(uniques.values)
        .map(key => {
          const current = uniques.values[key];
          return {
            ...current,
            color: this.getStyleIconColor(current),
            icon: current.externalGraphic && this.getStyleExternalGraphicIcon(current.externalGraphic),
          };
        })
        .concat([defaultStyle]);
    },
    getRanges(style) {
      const ranges = style.ranges;
      const defaultStyle = { ...style };
      defaultStyle.color = this.getStyleIconColor(defaultStyle);
      defaultStyle.icon =
        defaultStyle.externalGraphic && this.getStyleExternalGraphicIcon(defaultStyle.externalGraphic);
      if (!ranges.values) {
        return;
      }
      return ranges.values
        .map(value => {
          return {
            ...value,
            color: this.getStyleIconColor(value),
            icon: value.externalGraphic && this.getStyleExternalGraphicIcon(value.externalGraphic),
          };
        })
        .concat([defaultStyle]);
    },
    getModuleLayerType(item) {
      return this.getLayerType(item) || this.moduleLayer.geometry_type;
    },
    async getModuleStyle() {
      await this.initLegendItems();
      this.legendItems = [this.currentModuleStyle];
    },
    async init() {
      await this.getFeaturesLayer(this.layerId);
      await this.getModuleStyle();
      this.isFetching = false;
    },
  },
  mounted() {
    this.init();
  },
};
</script>

<style scoped></style>
